import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, CircularProgress, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import QuizContainer from './quiz/QuizContainer';
import SubjectList from './study/SubjectList';
import SubjectTermsDialog from './study/SubjectTermsDialog';
import WrongAnswerNotebook from './study/WrongAnswerNotebook';
import AddTermDialog from './study/AddTermDialog';
import MyTermDialog from './study/MyTermDialog';
import MyDefinitionDialog from './study/MyDefinitionDialog';
import AdminPanel from './AdminPanel';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import useStudyTimeTracker from '../hooks/useStudyTimeTracker';
import { supabase } from '../lib/supabase';

const QuizContent = () => {
  const { isAdmin } = useAuth();
  const {
    openSubjectTerms,
    setOpenSubjectTerms,
    openAddDialog,
    setOpenAddDialog,
    openAddMyTermDialog,
    setOpenAddMyTermDialog,
    openAddMyDefinitionDialog,
    setOpenAddMyDefinitionDialog,
    terms,
    selectedTerm,
    setSelectedTerm,
    selectedSubject,
    setSelectedSubject,
    wrongAnswers
  } = useApp();
  const { startTracking, stopTracking, elapsedMinutes, isTracking } = useStudyTimeTracker();
  const { user } = useAuth();
  const [hasStudyTicket, setHasStudyTicket] = useState(undefined);
  const [hasSubscription, setHasSubscription] = useState(undefined);

  const [sortOption, setSortOption] = useState('alphabet');
  const [userInput, setUserInput] = useState('');
  const subjects = terms ? Array.from(new Set(terms.map(term => term.subject))).filter(Boolean) : [];
  // Must Know term이 1개 이상 있으면 subject list에 'Must Know'를 추가
  if (terms && terms.some(term => term.is_must_know === true)) {
    if (!subjects.includes('Must Know')) {
      subjects.unshift('Must Know');
    }
  }

  const [initialized, setInitialized] = useState(false);
  const [timer, setTimer] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const timerRef = useRef(null);

  const handleSortOptionChange = (event) => {
    const newSortOption = event.target.value;
    setSortOption(newSortOption);
  };

  // is_must_know 값 타입을 느슨하게 체크하는 함수
  const isMustKnow = (val) => val === true || val === 'true' || val === 1;

  // 과목별 용어 개수 반환 함수 수정
  const getSubjectCount = (subject) => {
    if (subject === 'Must Know') {
      return terms.filter(term => isMustKnow(term.is_must_know)).length;
    }
    return terms.filter(term => term.subject === subject).length;
  };

  // 드롭다운(과목 선택) 핸들러: 바로 퀴즈 용어 변경
  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    const subjectTerms = terms.filter(term => 
      subject === 'all' ? true : term.subject === subject
    );
    if (subjectTerms.length > 0) {
      let nextTerm;
      switch (sortOption) {
        case 'random':
          const randomIndex = Math.floor(Math.random() * subjectTerms.length);
          nextTerm = subjectTerms[randomIndex];
          break;
        case 'wrong_first':
          const wrongTerms = subjectTerms.filter(term =>
            wrongAnswers.some(wrong => 
              wrong.term_id === term.id && 
              (subject === 'all' || wrong.subject === subject)
            )
          );
          nextTerm = wrongTerms.length > 0 ? wrongTerms[0] : subjectTerms[0];
          break;
        case 'alphabet':
        default:
          nextTerm = [...subjectTerms].sort((a, b) => 
            a.term.localeCompare(b.term, 'ko')
          )[0];
      }
      if (nextTerm) {
        setSelectedTerm(nextTerm);
      }
    }
  };

  // Subject 박스 클릭: 리스트(다이얼로그) 오픈
  const handleSubjectClick = (subject) => {
    setSelectedSubject(subject);
    setOpenSubjectTerms(true);
  };

  useEffect(() => {
    if (!initialized && terms && terms.length > 0 && subjects.length > 0) {
      // 랜덤 초기화
      const randomSubject = subjects[Math.floor(Math.random() * subjects.length)];
      setSelectedSubject(randomSubject);
      const sortOptions = ['alphabet', 'random', 'wrong_first'];
      const randomSort = sortOptions[Math.floor(Math.random() * sortOptions.length)];
      setSortOption(randomSort);
      const subjectTerms = terms.filter(term => term.subject === randomSubject);
      if (subjectTerms.length > 0) {
        const randomTerm = subjectTerms[Math.floor(Math.random() * subjectTerms.length)];
        setSelectedTerm(randomTerm);
      }
      setInitialized(true);
    }
  }, [initialized, terms, subjects]);

  useEffect(() => {
    async function fetchTicketStatus() {
      if (!user || !user.id) {
        setHasStudyTicket(false);
        setHasSubscription(false);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('study_ticket_remain,subscription_expire_at')
          .eq('id', user.id)
          .single();
        if (error) {
          console.error('권종 상태 fetch 에러:', error);
          setHasStudyTicket(false);
          setHasSubscription(false);
          return;
        }
        setHasStudyTicket((data?.study_ticket_remain || 0) > 0);
        setHasSubscription(data?.subscription_expire_at && new Date(data.subscription_expire_at) > new Date());
        console.log('권종 상태:', {
          study_ticket_remain: data?.study_ticket_remain,
          subscription_expire_at: data?.subscription_expire_at,
          hasStudyTicket: (data?.study_ticket_remain || 0) > 0,
          hasSubscription: data?.subscription_expire_at && new Date(data.subscription_expire_at) > new Date()
        });
      } catch (e) {
        console.error('권종 상태 fetch 예외:', e);
        setHasStudyTicket(false);
        setHasSubscription(false);
      }
    }
    fetchTicketStatus();
  }, [user]);

  // undefined일 때도 false로 처리
  const canSubmit = (hasStudyTicket === true || hasSubscription === true);

  console.log('권종 상태:', { hasStudyTicket, hasSubscription, canSubmit });

  // console.log('terms:', terms);
  // console.log('subjects:', subjects);
  // console.log('selectedSubject:', selectedSubject);
  // console.log('sortOption:', sortOption);

  // if (terms && terms.length > 0 && terms[0]) {
  //   console.log('terms[0] keys:', Object.keys(terms[0]));
  //   console.log('terms[0] full:', terms[0]);
  // }

  useEffect(() => {
    if (!hasStarted && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [hasStarted]);

  // 문제(용어)가 바뀔 때 타이머 초기화
  useEffect(() => {
    setTimer(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setHasStarted(false);
  }, [selectedTerm]);

  // 타이머 시작
  const startTimer = () => {
    if (!timerRef.current) {
      // console.log('타이머 시작');
      timerRef.current = setInterval(() => {
        setTimer((t) => t + 1);
      }, 1000);
      setHasStarted(true);
      startTracking();
    }
  };

  // 타이머 정지
  const stopTimer = () => {
    if (timerRef.current) {
      // console.log('타이머 정지');
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setHasStarted(false);
    // console.log('stopTimer 상태:', { timerRef: timerRef.current, hasStarted });
    stopTracking();
  };

  // 타이머 초기화
  const resetTimer = () => {
    // console.log('타이머 초기화');
    setTimer(0);
    clearInterval(timerRef.current);
    timerRef.current = null;
    setHasStarted(false);
  };

  useEffect(() => {
    return () => {
      stopTimer();
      stopTracking(); // 학습 시간 트래커도 정지
    };
  }, []);

  // 타이머 정지 + 학습 시간 트래커 정지 (제출 시)
  const handleQuizSubmit = () => {
    // console.log('handleQuizSubmit 호출');
    stopTimer();
    stopTracking();
    setHasStarted(false);
    setUserInput('');
  };

  // console.log('selectedTerm 변경:', selectedTerm);

  if (!terms) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        width: '100%',
        maxWidth: '100%', 
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 3, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {/* 다이얼로그들 */}
      {openAddDialog && (
        <AddTermDialog 
          open={openAddDialog}
          onClose={() => setOpenAddDialog(false)}
        />
      )}
      
      {openAddMyTermDialog && (
        <MyTermDialog
          open={openAddMyTermDialog}
          onClose={() => setOpenAddMyTermDialog(false)}
        />
      )}
      
      {openAddMyDefinitionDialog && (
        <MyDefinitionDialog
          open={openAddMyDefinitionDialog}
          onClose={() => setOpenAddMyDefinitionDialog(false)}
        />
      )}
      
      {openSubjectTerms && (
        <SubjectTermsDialog
          open={openSubjectTerms}
          onClose={() => setOpenSubjectTerms(false)}
        />
      )}
      
      {isAdmin && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            backgroundColor: 'background.paper',
            backdropFilter: 'blur(10px)',
            boxShadow: 3,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '4px',
              background: 'linear-gradient(90deg, primary.main, secondary.main)',
            }
          }}
        >
          <AdminPanel />
        </Paper>
      )}
      
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          backgroundColor: 'background.paper',
          backdropFilter: 'blur(10px)',
          boxShadow: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: 'linear-gradient(90deg, primary.main, secondary.main)',
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          {userInput && hasStarted && (
            <Typography sx={{ fontSize: '0.95rem', color: 'text.secondary', fontWeight: 500 }}>
              풀이 시간: {timer}초
            </Typography>
          )}
        </Box>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 3,
          gap: 2
        }}>
          <Typography variant="h5" component="h2" fontWeight={700} color="text.primary">
            학습 퀴즈
            <Box component="span" sx={{ 
              display: 'inline-block',
              ml: 1,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: '0.7em',
              py: 0.5,
              px: 1.5,
              borderRadius: 10,
              verticalAlign: 'middle'
            }}>
              {terms?.length || 0} 용어
            </Box>
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>과목 선택</InputLabel>
              <Select
                value={selectedSubject || 'all'}
                label="과목 선택"
                onChange={(e) => handleSubjectSelect(e.target.value)}
                sx={{
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  fontWeight: 600,
                  '& .MuiSelect-select': {
                    py: 1.2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  },
                  '& fieldset': {
                    borderColor: 'primary.light',
                    borderWidth: 2,
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 3,
                      boxShadow: 6,
                      mt: 1,
                      bgcolor: 'background.paper',
                      minWidth: 180,
                    },
                  },
                  MenuListProps: {
                    sx: {
                      py: 0.5,
                    },
                  },
                }}
              >
                <MenuItem
                  value="all"
                  sx={{
                    fontWeight: 600,
                    borderRadius: 2,
                    mx: 1,
                    my: 0.5,
                    '&.Mui-selected': {
                      bgcolor: 'primary.light',
                      color: 'primary.main',
                    },
                    '&:hover': {
                      bgcolor: 'primary.lighter',
                    },
                  }}
                >
                  전체 보기
                </MenuItem>
                {subjects.map((subject) => (
                  <MenuItem
                    key={subject}
                    value={subject}
                    sx={{
                      fontWeight: 600,
                      borderRadius: 2,
                      mx: 1,
                      my: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        color: 'primary.main',
                      },
                      '&:hover': {
                        bgcolor: 'primary.lighter',
                      },
                    }}
                  >
                    {subject}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>정렬 옵션</InputLabel>
              <Select
                value={sortOption}
                label="정렬 옵션"
                onChange={handleSortOptionChange}
                sx={{
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  fontWeight: 600,
                  '& .MuiSelect-select': {
                    py: 1.2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                  },
                  '& fieldset': {
                    borderColor: 'primary.light',
                    borderWidth: 2,
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 3,
                      boxShadow: 6,
                      mt: 1,
                      bgcolor: 'background.paper',
                      minWidth: 180,
                    },
                  },
                  MenuListProps: {
                    sx: {
                      py: 0.5,
                    },
                  },
                }}
              >
                <MenuItem
                  value="alphabet"
                  sx={{
                    fontWeight: 600,
                    borderRadius: 2,
                    mx: 1,
                    my: 0.5,
                    '&.Mui-selected': {
                      bgcolor: 'primary.light',
                      color: 'primary.main',
                    },
                    '&:hover': {
                      bgcolor: 'primary.lighter',
                    },
                  }}
                >
                  알파벳순
                </MenuItem>
                <MenuItem
                  value="random"
                  sx={{
                    fontWeight: 600,
                    borderRadius: 2,
                    mx: 1,
                    my: 0.5,
                    '&.Mui-selected': {
                      bgcolor: 'primary.light',
                      color: 'primary.main',
                    },
                    '&:hover': {
                      bgcolor: 'primary.lighter',
                    },
                  }}
                >
                  랜덤순
                </MenuItem>
                <MenuItem
                  value="wrong_first"
                  sx={{
                    fontWeight: 600,
                    borderRadius: 2,
                    mx: 1,
                    my: 0.5,
                    '&.Mui-selected': {
                      bgcolor: 'primary.light',
                      color: 'primary.main',
                    },
                    '&:hover': {
                      bgcolor: 'primary.lighter',
                    },
                  }}
                >
                  오답먼저
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <QuizContainer
          sortOption={sortOption} 
          selectedSubject={selectedSubject || 'all'}
          onSubjectChange={setSelectedSubject}
          subjects={subjects}
          onSortOptionChange={handleSortOptionChange}
          onStart={startTimer}
          onSubmit={handleQuizSubmit}
          onNext={resetTimer}
          timer={timer}
          hasStarted={hasStarted}
          selectedTerm={selectedTerm}
          setSelectedTerm={setSelectedTerm}
          onSelectTerm={setSelectedTerm}
          userInput={userInput}
          setUserInput={setUserInput}
          canSubmit={canSubmit}
        />
      </Paper>
      
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          backgroundColor: 'background.paper',
          backdropFilter: 'blur(10px)',
          boxShadow: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          position: 'relative',
          width: '100%',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: 'linear-gradient(90deg, primary.main, secondary.main)',
          }
        }}
      >
        <Typography variant="h5" component="h2" fontWeight={700} color="text.primary" mb={3}>
          학습 주제
          <Box component="span" sx={{ 
            display: 'inline-block',
            ml: 1,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            fontSize: '0.7em',
            py: 0.5,
            px: 1.5,
            borderRadius: 10,
            verticalAlign: 'middle'
          }}>
            {subjects?.length || 0} 과목
          </Box>
        </Typography>
        <SubjectList
          subjects={subjects}
          getSubjectCount={getSubjectCount}
          onSubjectClick={handleSubjectClick}
        />
      </Paper>
    </Box>
  );
};

export default QuizContent; 